import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Sun, Moon, Check, User, Volume2, VolumeX, ArrowRight,
  FolderKanban, ShieldAlert, GraduationCap, Dumbbell, Users, UserCog,
  FileText, ListChecks, BookOpen, AlertTriangle, Eraser,
} from "lucide-react";
import { useState } from "react";
import { isSoundEnabled, setSoundEnabled, previewSuccess } from "@/lib/sound";

export default function Configuracoes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { theme, toggleTheme } = useTheme();
  const [soundOn, setSoundOn] = useState(isSoundEnabled());

  const toggleSound = (v) => { setSoundOn(v); setSoundEnabled(v); if (v) previewSuccess(); };

  const roleLabel = isAdmin ? "Administrador" : "Instrutor";
  const grau = user?.grau_nome && user.grau_nome !== "Não se aplica" ? user.grau_nome : "";
  const nome = [grau, user?.nome_guerra || user?.name].filter(Boolean).join(" ");

  const GRUPOS = [
    {
      titulo: "Organização das instruções",
      itens: [
        { to: "/cursos", label: "Cursos", icon: FolderKanban, color: "text-sky-400" },
        { to: "/instrucoes", label: "Instruções", icon: ShieldAlert, color: "text-amber-400" },
        { to: "/turmas", label: "Turmas", icon: GraduationCap, color: "text-purple-400" },
        { to: "/oficinas", label: "Oficinas", icon: Dumbbell, color: "text-emerald-400" },
      ],
    },
    {
      titulo: "Pessoas",
      itens: [
        { to: "/alunos", label: "Alunos", icon: Users, color: "text-amber-400" },
        { to: "/instrutores", label: "Instrutores", icon: UserCog, color: "text-teal-400", adminOnly: true },
      ],
    },
    {
      titulo: "Modelos de avaliação",
      itens: [
        { to: "/modelos", label: "Fichas de avaliação, critérios e escalas", icon: FileText, color: "text-sky-400", adminOnly: true },
        { to: "/formativa/modelo", label: "Modelo formativo", icon: ListChecks, color: "text-purple-400", adminOnly: true },
      ],
    },
    {
      titulo: "Conteúdo e registros",
      itens: [
        { to: "/biblioteca", label: "Biblioteca doutrinária", icon: BookOpen, color: "text-emerald-400" },
        { to: "/ocorrencias", label: "Ocorrências", icon: AlertTriangle, color: "text-orange-400" },
      ],
    },
    {
      titulo: "Administração",
      adminOnly: true,
      itens: [
        { to: "/instrutores", label: "Usuários e permissões", icon: UserCog, color: "text-teal-400", testid: "config-link-usuarios-permissoes" },
        { to: "/limpeza", label: "Limpeza de dados", icon: Eraser, color: "text-red-400" },
      ],
    },
  ];

  return (
    <div data-testid="configuracoes-page">
      <PageHeader title="Configurações" subtitle="Gerência, cadastros e preferências do aplicativo" />

      <div className="space-y-8">
        {GRUPOS.map((g) => {
          if (g.adminOnly && !isAdmin) return null;
          const itens = g.itens.filter((i) => !i.adminOnly || isAdmin);
          if (!itens.length) return null;
          return (
            <div key={g.titulo}>
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-3 text-muted-foreground">{g.titulo}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {itens.map((i) => (
                  <Card key={g.titulo + i.to} role="button" tabIndex={0} data-testid={i.testid || `config-link-${i.to.replace(/\//g, "")}`}
                    onClick={() => navigate(i.to)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(i.to); }}
                    className="p-4 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-all active:scale-[0.98] flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary shrink-0"><i.icon className={`h-5 w-5 ${i.color}`} /></div>
                    <div className="font-semibold truncate flex-1 min-w-0">{i.label}</div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </Card>
                ))}
              </div>
            </div>
          );
        })}

        {/* Preferências pessoais — disponíveis a todos os usuários */}
        <div>
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-3 text-muted-foreground">Preferências</h2>

          <Card className="p-5 bg-card border-border mb-3">
            <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-1">Aparência</h3>
            <p className="text-sm text-muted-foreground mb-4">Escolha o tema de exibição do aplicativo.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: "dark", label: "Tema escuro", desc: "Identidade padrão do MÉTRON", icon: Moon },
                { value: "light", label: "Tema claro", desc: "Alto contraste em ambientes claros", icon: Sun },
              ].map((o) => {
                const ativo = theme === o.value;
                return (
                  <button key={o.value} data-testid={`tema-${o.value}`} onClick={() => { if (!ativo) toggleTheme(); }}
                    className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${ativo ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border hover:bg-secondary/50"}`}>
                    <div className={`p-2 rounded-lg shrink-0 ${ativo ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}><o.icon className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold flex items-center gap-2">{o.label}{ativo && <Check className="h-4 w-4 text-primary" />}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 break-words">{o.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="p-5 bg-card border-border mb-3">
            <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-1">Sons da interface</h3>
            <p className="text-sm text-muted-foreground mb-4">Feedback sonoro discreto ao concluir operações e em erros. Não toca durante a avaliação.</p>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`p-2 rounded-lg shrink-0 ${soundOn ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  {soundOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold">{soundOn ? "Ligado" : "Desligado"}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Preferência salva neste dispositivo/PWA.</div>
                </div>
              </div>
              <Switch data-testid="toggle-sons" checked={soundOn} onCheckedChange={toggleSound} />
            </div>
          </Card>

          <Card className="p-5 bg-card border-border">
            <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-4">Conta</h3>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-full bg-primary/15 text-primary shrink-0"><User className="h-5 w-5" /></div>
              <div className="min-w-0">
                <div className="font-medium truncate">{nome}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email} · {roleLabel}</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
